// 延遲函數
export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// 重試機制
export const withRetry = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error
      
      // 如果是 429 錯誤且還有重試次數，則等待後重試
      if (error instanceof Error && error.message.includes('429') && attempt < maxRetries) {
        const delayTime = baseDelay * Math.pow(2, attempt) // 指數退避
        console.warn(`API rate limited, retrying in ${delayTime}ms... (attempt ${attempt + 1}/${maxRetries + 1})`)
        await delay(delayTime)
        continue
      }
      
      // 其他錯誤或已達最大重試次數，直接拋出
      throw error
    }
  }
  
  throw lastError!
}

// 處理 API 回應
export const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const errorMessage = errorData.message || `HTTP error! status: ${response.status}`
    
    // 為 429 錯誤添加特殊標記
    if (response.status === 429) {
      throw new Error(`429: ${errorMessage}`)
    }
    
    // 為 401 錯誤添加特殊標記
    if (response.status === 401) {
      throw new Error(`401: ${errorMessage}`)
    }
    
    throw new Error(errorMessage)
  }
  return response.json()
}

// 取得認證標頭
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  }
}

// API 基礎 URL
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001'

// 時區偵測和處理函數
export const getTimezoneOffset = (): number => {
  return new Date().getTimezoneOffset()
}

export const getTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    console.warn('Cannot detect timezone, using offset instead:', error)
    return `UTC${getTimezoneOffset() > 0 ? '-' : '+'}${Math.abs(getTimezoneOffset() / 60)}`
  }
}

// 在 API 請求中添加時區信息
export const addTimezoneToParams = (params: URLSearchParams): URLSearchParams => {
  params.set('timezoneOffset', getTimezoneOffset().toString())
  params.set('timezone', getTimezone())
  return params
}

// 在 API 請求中添加時區信息到查詢字符串
export const addTimezoneToUrl = (url: string): string => {
  // 處理相對路徑，避免 new URL() 的問題
  const searchParams = new URLSearchParams()
  
  // 解析現有的查詢參數（如果有的話）
  const [path, existingQuery] = url.split('?')
  if (existingQuery) {
    const existing = new URLSearchParams(existingQuery)
    existing.forEach((value, key) => {
      searchParams.append(key, value)
    })
  }
  
  // 添加時區信息，確保正確編碼
  searchParams.set('timezoneOffset', getTimezoneOffset().toString())
  searchParams.set('timezone', encodeURIComponent(getTimezone()))
  
  return `${path}?${searchParams.toString()}`
}

// 刷新Token的函數
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken')
  
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken })
  })

  if (!response.ok) {
    throw new Error('Failed to refresh token')
  }

  const result = await response.json()
  const newAccessToken = result.data.accessToken
  
  // 更新localStorage中的token
  localStorage.setItem('token', newAccessToken)
  
  return newAccessToken
}

// 帶有自動刷新功能的API請求函數
export const apiRequestWithAutoRefresh = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data: T; message?: string }> => {
  const url = API_BASE_URL === '/api' && endpoint.startsWith('/api') 
    ? endpoint 
    : API_BASE_URL.endsWith('/api') && endpoint.startsWith('/api')
    ? endpoint
    : `${API_BASE_URL}${endpoint}`
  
  const makeRequest = async (headers: HeadersInit = {}) => {
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      }
    }
    
    const response = await fetch(url, requestOptions)
    
    if (response.status === 401) {
      console.log('Received 401, attempting token refresh...')
      // 如果是認證失敗，嘗試刷新token
      try {
        const newToken = await refreshAccessToken()
        console.log('Token refresh successful, retrying request...')
        // 用新token重新發送請求
        const newHeaders = {
          ...headers,
          'Authorization': `Bearer ${newToken}`
        }
        const retryResponse = await fetch(url, {
          ...requestOptions,
          headers: {
            ...newHeaders,
            ...options.headers
          }
        })
        return handleResponse(retryResponse)
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        // 刷新失敗，清除所有token並拋出錯誤
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        
        // 觸發頁面刷新以重新初始化認證狀態
        setTimeout(() => {
          window.location.reload()
        }, 1000)
        
        throw new Error('登入已過期，請重新登入')
      }
    }
    
    return handleResponse(response)
  }

  return withRetry(async () => {
    return makeRequest(getAuthHeaders())
  })
}

// 通用 API 請求函數
export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data: T; message?: string }> => {
  // 如果 API_BASE_URL 是 '/api' 且 endpoint 已經以 '/api' 開頭，直接使用 endpoint
  // 否則正常拼接
  const url = API_BASE_URL === '/api' && endpoint.startsWith('/api') 
    ? endpoint 
    : API_BASE_URL.endsWith('/api') && endpoint.startsWith('/api')
    ? endpoint  // 避免重複的 /api
    : `${API_BASE_URL}${endpoint}`
  
  const defaultOptions: RequestInit = {
    headers: getAuthHeaders(),
    ...options
  }

  return withRetry(async () => {
    const response = await fetch(url, defaultOptions)
    return handleResponse(response)
  })
}
